import { useState, useEffect, FC, ReactNode, useRef, useCallback } from 'react';
import { FireTVCampaignAutomationClient } from '@amzn/fire-tv-campaign-automation-service';
import { ClientContext } from './ClientContext';
import { useAuthentication } from './AuthenticationContext';
import { ProvisionedConstants } from 'src/constants';
import { LoadingOverlay } from 'src/components/general/LoadingOverlay';
import { logger } from 'src/logger';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { ErrorDisplay } from 'src/components/general/ErrorDisplay';
import { Hub } from 'aws-amplify/utils';
import { ControlPlaneClientUtils } from 'src/utils/clients/controlPlane';

interface ClientContextProviderProps {
  children: ReactNode;
}

const INITIALIZING = 'Initializing...';

/**
 * Provider component that manages the FireTV Campaign Automation client instance.
 *
 * This component:
 * 1. Initializes the control plane client when authentication is available
 * 2. Handles client cleanup when authentication changes
 * 3. Provides error handling for client initialization failures
 * 4. Makes the client instance available to child components via Context
 * 5. Properly cleans up async operations on unmount using AbortController
 *
 * @component
 * @example
 * // Wrap your app with the provider
 * <ClientContextProvider>
 *   <App />
 * </ClientContextProvider>
 */
export const ClientContextProvider: FC<ClientContextProviderProps> = ({ children }) => {
  // Internal state to manage the control plane client instance
  const [controlPlaneClient, setControlPlaneClient] = useState<FireTVCampaignAutomationClient>();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Refs for cleanup and managing component lifecycle
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Get authentication session from the Authentication Context
  const { authSession } = useAuthentication();

  /**
   * Schedules a timer to refresh credentials before they expire
   * @param expiresAt - Expiration date of current credentials
   */
  const scheduleCredentialRefresh = useCallback(
    (expiresAt: Date | undefined) => {
      // Clear existing timer if any
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }

      // Don't schedule if no expiration date
      if (!expiresAt) {
        logger.warn('No expiration date provided for credentials');
        return;
      }

      // Calculate time until expiry minus 5 minute buffer
      const timeUntilExpiry = expiresAt.getTime() - Date.now() - 5 * 60 * 1000;

      if (timeUntilExpiry <= 0) {
        logger.warn('Credentials already expired or will expire soon', {
          expiresAt: expiresAt.toISOString(),
          timeUntilExpiryMs: timeUntilExpiry,
        });
        return;
      }

      logger.debug('Scheduling credential refresh', {
        expiresAt: expiresAt.toISOString(),
        timeUntilExpiryMs: timeUntilExpiry,
        refreshAt: new Date(Date.now() + timeUntilExpiry).toISOString(),
      });

      // Schedule refresh
      expirationTimerRef.current = setTimeout(() => {
        logger.info('Proactively refreshing client before credentials expire');
        initializeClient();
      }, timeUntilExpiry);
    },
    [], // Empty dependency array since we use refs
  );

  /**
   * Initialize the client when authentication is available or changed
   * Uses the shared AbortController to prevent state updates after unmount
   */
  const initializeClient = useCallback(async () => {
    // Reset state when auth session is removed
    if (!authSession) {
      setControlPlaneClient(undefined);
      setIsInitializing(false);
      return;
    }

    // If there's no abort controller or it's been aborted, don't proceed
    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
      return;
    }
    const signal = abortControllerRef.current.signal;

    try {
      logger.debug('Initializing control plane client');
      // Initialize the control plane client with authentication and configuration
      const { client, expiresAt } = await ControlPlaneClientUtils.getControlPlaneClient({
        authSession,
        crossAccountRoleArn: ProvisionedConstants.CONTROL_PLANE_CROSS_ACCOUNT_ROLE_ARN,
        controlPlaneRegion: ProvisionedConstants.CONTROL_PLANE_REGION,
        userPoolRegion: ProvisionedConstants.USER_POOL_REGION,
        controlPlaneEndpoint: ProvisionedConstants.CONTROL_PLANE_ENDPOINT,
      });

      // Only update state if the component is still mounted
      if (!signal?.aborted) {
        logger.debug('Setting client in state', { client });
        setControlPlaneClient(client);

        // Schedule credential refresh if we have an expiration date
        if (expiresAt) {
          logger.debug('Setting up credential refresh', { expiresAt: expiresAt.toISOString() });
          scheduleCredentialRefresh(expiresAt);
        }
      }
    } catch (error) {
      // Only update state and log error if the component is still mounted
      if (!signal?.aborted) {
        logger.error('Failed to initialize FireTVCampaignAutomationServiceClient:', { error });
        setError(getErrorMessage(error));
        setControlPlaneClient(undefined);
      }
    } finally {
      if (!signal?.aborted) {
        setIsInitializing(false);
      }
    }
  }, [authSession, scheduleCredentialRefresh]);

  useEffect(() => {
    // Reset the abort controller in case there was a previous one
    abortControllerRef.current = new AbortController();

    // Reset state and start initialization
    setIsInitializing(true);
    initializeClient();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      // If we get a token refresh event and we have an auth session, reinitialize the client
      if (event === 'tokenRefresh' && authSession) {
        logger.debug('Token refresh detected, reinitializing control plane client');
        initializeClient();
      }
    });

    // Cleanup function to abort any pending operations when the component unmounts
    return () => {
      logger.debug('Cleaning up client context');
      // Abort any in-progress operations
      abortControllerRef.current.abort();
      unsubscribe();

      // Clear any scheduled refresh
      if (expirationTimerRef.current) {
        clearTimeout(expirationTimerRef.current);
        expirationTimerRef.current = null;
      }
    };
  }, [authSession, initializeClient]);

  if (error) {
    return <ErrorDisplay onNavigateHome={() => window.location.reload()} />;
  }

  if (isInitializing || !controlPlaneClient) {
    return <LoadingOverlay text={INITIALIZING} />;
  }

  return <ClientContext.Provider value={{ controlPlaneClient }}>{children}</ClientContext.Provider>;
};
